import {ENDPOINTS} from '../common/endpoints';
import {GuidLightDto} from '../models/common/IGuidLight';
import {RecipeForOrderGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import {RecipeGuidDto, RecipeGuidListParams, RecipeGuidListResponse, RecipePartGuidDto, SaveRecipeGuidRequest, SaveRecipePartGuidRequest} from '../models/Recipe/IRecipeGuid';
import {IRecipePartCategory} from '../models/Recipe/IRecipePartCategory';
import {baseAPI} from './BaseAPI';
import {TAGS} from "./TagTypes";

export const recipeAPI = baseAPI.injectEndpoints({
    endpoints: (build) => ({
        getAvailableRecipeNames: build.query<string[], void>({
            query: () => ({url: ENDPOINTS.RECIPES.AVAILABLE_NAMES, method: 'GET'}),
            providesTags: [TAGS.RECIPES],
        }),
        getRecipesV2: build.query<RecipeGuidListResponse, RecipeGuidListParams>({
            query: params => ({url: ENDPOINTS.RECIPES.V2, method: 'GET', params}),
            providesTags: [TAGS.RECIPES],
        }),
        getRecipesV2AllLight: build.query<GuidLightDto[], void>({
            query: () => ({url: ENDPOINTS.RECIPES.V2_ALL_LIGHT, method: 'GET'}),
            providesTags: [TAGS.RECIPES_LIGHT],
        }),
        getRecipesForOrderV2: build.query<RecipeForOrderGuidDto[], void>({
            query: () => ({url: ENDPOINTS.RECIPES.V2_FOR_ORDER, method: 'GET'}),
            providesTags: [TAGS.RECIPES_FOR_ORDER],
        }),
        getRecipeV2: build.query<RecipeGuidDto, string>({
            query: id => ({url: `${ENDPOINTS.RECIPES.V2}/${id}`, method: 'GET'}),
            providesTags: (_result, _error, id) => [{type: TAGS.RECIPE, id}],
        }),
        createRecipeV2: build.mutation<RecipeGuidDto, SaveRecipeGuidRequest>({
            query: body => ({url: ENDPOINTS.RECIPES.V2, method: 'POST', body}),
            invalidatesTags: [TAGS.RECIPES, TAGS.RECIPES_LIGHT, TAGS.RECIPES_FOR_ORDER],
        }),
        updateRecipeV2: build.mutation<RecipeGuidDto, {id: string; body: SaveRecipeGuidRequest}>({
            query: ({id, body}) => ({url: `${ENDPOINTS.RECIPES.V2}/${id}`, method: 'PUT', body}),
            invalidatesTags: (_result, _error, {id}) => [TAGS.RECIPES, {type: TAGS.RECIPE, id}, TAGS.RECIPES_LIGHT, TAGS.RECIPES_FOR_ORDER],
        }),
        deleteRecipeV2: build.mutation<void, string>({
            query: id => ({url: `${ENDPOINTS.RECIPES.V2}/${id}`, method: 'DELETE'}),
            // Do not invalidate the active detail query: after DELETE it would
            // immediately refetch the removed recipe before navigation unmounts it.
            invalidatesTags: [TAGS.RECIPES, TAGS.RECIPE_PARTS, TAGS.RECIPES_LIGHT, TAGS.RECIPES_FOR_ORDER],
        }),
        getRecipePartsV2: build.query<RecipePartGuidDto[], void>({
            query: () => ({url: ENDPOINTS.RECIPES.V2_PARTS, method: 'GET'}),
            providesTags: [TAGS.RECIPE_PARTS],
        }),
        getRecipePartV2: build.query<RecipePartGuidDto, string>({
            query: id => ({url: `${ENDPOINTS.RECIPES.V2_PARTS}/${id}`, method: 'GET'}),
            providesTags: [TAGS.RECIPE_PARTS],
        }),
        createRecipePartV2: build.mutation<RecipePartGuidDto, SaveRecipePartGuidRequest>({
            query: body => ({url: ENDPOINTS.RECIPES.V2_PARTS, method: 'POST', body}),
            invalidatesTags: [TAGS.RECIPE_PARTS],
        }),
        updateRecipePartV2: build.mutation<RecipePartGuidDto, {id: string; body: SaveRecipePartGuidRequest}>({
            query: ({id, body}) => ({url: `${ENDPOINTS.RECIPES.V2_PARTS}/${id}`, method: 'PUT', body}),
            invalidatesTags: [TAGS.RECIPE_PARTS, TAGS.RECIPE, TAGS.RECIPES, TAGS.RECIPES_FOR_ORDER],
        }),
        deleteRecipePartV2: build.mutation<void, string>({
            query: id => ({url: `${ENDPOINTS.RECIPES.V2_PARTS}/${id}`, method: 'DELETE'}),
            invalidatesTags: [TAGS.RECIPE_PARTS],
        }),
        attachRecipePartV2: build.mutation<void, {recipeId: string; partId: string; isMainPart: boolean; sortOrder: number}>({
            query: ({recipeId, partId, ...params}) => ({url: `${ENDPOINTS.RECIPES.V2}/${recipeId}/parts/${partId}`, method: 'POST', params}),
            invalidatesTags: [TAGS.RECIPE_PARTS, TAGS.RECIPE, TAGS.RECIPES],
        }),
        detachRecipePartV2: build.mutation<void, {recipeId: string; partId: string}>({
            query: ({recipeId, partId}) => ({url: `${ENDPOINTS.RECIPES.V2}/${recipeId}/parts/${partId}`, method: 'DELETE'}),
            invalidatesTags: [TAGS.RECIPE_PARTS, TAGS.RECIPE, TAGS.RECIPES, TAGS.RECIPES_FOR_ORDER],
        }),
        getRecipePartCategories: build.query<IRecipePartCategory[], void>({
            query: () => ({url: ENDPOINTS.RECIPES.RECIPE_PART_CATEGORIES, method: 'GET'}),
            providesTags: [TAGS.RECIPE_PART_CATEGORIES],
        }),
        createRecipePartCategory: build.mutation<IRecipePartCategory, {name: string}>({
            query: body => ({url: ENDPOINTS.RECIPES.RECIPE_PART_CATEGORIES, method: 'POST', body}),
            invalidatesTags: [TAGS.RECIPE_PART_CATEGORIES],
        }),
        updateRecipePartCategory: build.mutation<IRecipePartCategory, {id: string; name: string}>({
            query: ({id, name}) => ({url: `${ENDPOINTS.RECIPES.RECIPE_PART_CATEGORIES}/${id}`, method: 'PUT', body: {name}}),
            invalidatesTags: [TAGS.RECIPE_PART_CATEGORIES, TAGS.RECIPE_PARTS],
        }),
        deleteRecipePartCategory: build.mutation<void, string>({
            query: id => ({url: `${ENDPOINTS.RECIPES.RECIPE_PART_CATEGORIES}/${id}`, method: 'DELETE'}),
            invalidatesTags: [TAGS.RECIPE_PART_CATEGORIES, TAGS.RECIPE_PARTS, TAGS.RECIPE],
        }),

    }),
});
